import React, { useEffect, useState } from 'react'
import {
  ActionArea,
  Button,
  Grid,
  Heading,
  Spinner,
} from '@design-systems/canvas-ui'
import Icon, { ArrowUpRightIcon, InfoIcon } from '@design-systems/react-icons'
import { useQuery } from '@apollo/client/react'
import SearchResultItem from './SearchResultItem'
import { FEDERATED_GET_GLOBAL_SEARCHES } from '../../../../graphql/queries/globalSearch/globalSearchQueries'
import { getResourceLink } from '../../utils/globalSearchHelpers'
import React$1 from 'react'
import { useNavigate } from 'react-router-dom'
import { getRefineSearchLink } from '../../utils/globalSearchHelpers'

type Props = {
  name: string
  domain: string
  searchText: string
  refineSearchLink: string
  icon?: React$1.FunctionComponent<null>
  iconColor?: string
  searchRefinementThreshold?: number
}

export type SearchResultsItem = {
  id?: string
  domain: string
  description: string
  nameSlot?: React.ReactNode
  captionSlot?: React.ReactNode
  icon?: React$1.FunctionComponent<null>
  iconColor?: string
}

export type GlobalSearchDomainResultsPage = {
  domain: string
  content: SearchResultsItem[]
  pageInfo: {
    totalElements: number
  }
}

export type GlobalSearchResponse = {
  globalSearch: {
    results: GlobalSearchDomainResultsPage[]
  }
}

const SearchResultsGroup: React.FC<Props> = ({
  name,
  domain,
  searchText,
  refineSearchLink,
  icon,
  iconColor,
  searchRefinementThreshold = 50,
}) => {
  const FETCH_PAGE_SIZE = 30
  const FIRST_PAGE_VISIBLE_COUNT = 6
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(0)
  const [visibleCount, setVisibleCount] = useState(FIRST_PAGE_VISIBLE_COUNT)

  useEffect(() => {
    setCurrentPage(0)
    setVisibleCount(FIRST_PAGE_VISIBLE_COUNT)
  }, [domain, searchText])

  const { data, loading, error, fetchMore } = useQuery(
    FEDERATED_GET_GLOBAL_SEARCHES,
    {
      variables: {
        domain,
        page: 0,
        searchText,
        size: FETCH_PAGE_SIZE,
      },
      skip: !searchText,
      notifyOnNetworkStatusChange: true,
    },
  )

  const domainResult = data?.globalSearch?.results?.find(
    (result) => result.domain === domain,
  )
  const content = domainResult?.content ?? []
  const visibleContent = content.slice(0, visibleCount)
  const totalElements = domainResult?.pageInfo?.totalElements ?? 0
  const visiblePage = Math.floor(
    Math.max(visibleCount - 1, 0) / FETCH_PAGE_SIZE,
  )

  const handleFetchMore = () => {
    const firstPageVisibleTarget = Math.min(
      FETCH_PAGE_SIZE,
      content.length,
      totalElements,
    )

    if (visibleCount < firstPageVisibleTarget) {
      setVisibleCount(firstPageVisibleTarget)
      return
    }

    const nextPage = currentPage + 1
    const hasNextPage = content.length < totalElements

    if (!hasNextPage) {
      return
    }

    void fetchMore({
      variables: {
        domain,
        page: nextPage,
        searchText,
        size: FETCH_PAGE_SIZE,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev

        const prevBucket = prev.globalSearch?.results?.[0]
        const moreBucket = fetchMoreResult.globalSearch?.results?.[0]
        if (!prevBucket || !moreBucket) return prev

        return {
          ...prev,
          globalSearch: {
            ...prev.globalSearch,
            results: [
              {
                ...prevBucket,
                content: [...prevBucket.content, ...moreBucket.content],
                pageInfo: moreBucket.pageInfo,
              },
            ],
          },
        }
      },
    }).then(() => {
      const nextVisibleCount = Math.min(
        visibleCount + FETCH_PAGE_SIZE,
        totalElements,
      )
      setCurrentPage(nextPage)
      setVisibleCount(nextVisibleCount)
    })
  }

  const hasHiddenFetchedItems = visibleCount < content.length
  const hasNextPage = content.length < totalElements
  const showFetchMore =
    visibleContent.length > 0 && (hasNextPage || hasHiddenFetchedItems)
  const showEndOfResults =
    visibleContent.length > 0 && !showFetchMore && visibleCount >= totalElements
  const showHintText =
    visibleContent.length > searchRefinementThreshold ||
    visiblePage >= 2 ||
    showEndOfResults

  if (!loading && totalElements === 0) {
    return null
  }

  return (
    <>
      <Grid.Container justify="space-between">
        <Grid.Item className="hc8-mb-4x">
          <Heading>{name}</Heading>
        </Grid.Item>
        <Grid.Item>
          <Button
            variant="secondary"
            onClick={() =>
              navigate(getRefineSearchLink(domain, searchText) ?? '#')
            }
          >
            Refine search
            <Icon svg={ArrowUpRightIcon} />
          </Button>
        </Grid.Item>
      </Grid.Container>

      <Grid.Container spacing="none" className="hc8-mb-6x">
        {visibleContent?.map((item, index) => (
          <Grid.Item justify="left" size={{ xs: 2 }} key={index}>
            <ActionArea
              corners="all"
              className="hc8-pa-2x"
              onClick={() => {
                const { url } = getResourceLink(item.id ?? '', item.domain)
                navigate(url)
              }}
            >
              <SearchResultItem
                nameSlot={item.id}
                captionSlot={item.description}
                icon={icon}
                iconColor={iconColor}
                domain={item.domain}
                description={item.description}
              />
            </ActionArea>
          </Grid.Item>
        ))}
      </Grid.Container>
      <Grid.Container className="hc8-mb-6x" align="center">
        {!content?.length && !loading && !error && (
          <Grid.Item size={{ xs: 12 }} align="center">
            <Heading size={5}>No results found</Heading>
          </Grid.Item>
        )}
        {loading && !error && (
          <Grid.Container size={{ xs: 12 }} justify="center">
            <Grid.Item>
              <Spinner />
            </Grid.Item>
          </Grid.Container>
        )}
        {showFetchMore && !loading && !error && (
          <Grid.Item size={{ xs: 12 }} align="center">
            <Button variant="ghost" onClick={() => handleFetchMore()}>
              Show more
            </Button>
          </Grid.Item>
        )}
        {content.length > 0 && showEndOfResults && (
          <Grid.Item size={{ xs: 12 }} align="center">
            <Heading size={5}>End of results</Heading>
          </Grid.Item>
        )}
        {showHintText && content?.length > 0 && (
          <Grid.Item size={{ xs: 12 }} align="center">
            <Icon svg={InfoIcon} />{' '}
            <span>
              Try <a href={refineSearchLink}>refining your search</a> for the{' '}
              best results
            </span>
          </Grid.Item>
        )}
        {error && (
          <Grid.Item size={{ xs: 12 }} align="center">
            <Heading size={5}>Something went wrong</Heading>
          </Grid.Item>
        )}
      </Grid.Container>
    </>
  )
}

export default SearchResultsGroup
